import { initSearchSuggest } from "../Search/SearchSuggest";

/**
 * Storefront mobile menu (#36899). At ≤900px the header's .sf-nav bar is restyled by
 * storefront.css into an off-canvas drawer; this toggles it open/closed, locks page
 * scroll while it is open, and returns focus to the hamburger on close.
 * Mounted globally from the layout. On desktop the toggle is hidden, so this is inert.
 */
class StorefrontMenu {
    private nav: HTMLElement;
    private overlay: HTMLElement;
    private toggle: HTMLButtonElement;
    private closeBtn: HTMLButtonElement;

    constructor() {
        this.nav = document.getElementById("sf-nav") as HTMLElement;
        this.overlay = document.getElementById("sf-nav-overlay") as HTMLElement;
        this.toggle = document.getElementById("sf-menu-open") as HTMLButtonElement;
        this.closeBtn = document.getElementById("sf-menu-close") as HTMLButtonElement;
        if (!this.nav || !this.toggle) return;

        this.toggle.addEventListener("click", () => this.open());
        this.closeBtn?.addEventListener("click", () => this.close());
        this.overlay?.addEventListener("click", () => this.close());
        document.addEventListener("keydown", (e) => { if (e.key === "Escape" && this.isOpen()) this.close(); });

        // Rotating a tablet / resizing past the breakpoint must not leave the page scroll-locked.
        window.matchMedia("(min-width: 901px)").addEventListener("change", (e) => { if (e.matches) this.close(false); });
    }

    private isOpen(): boolean {
        return this.nav.classList.contains("open");
    }

    private open(): void {
        this.nav.classList.add("open");
        this.overlay?.classList.add("open");
        document.body.classList.add("sf-menu-open");
        this.toggle.setAttribute("aria-expanded", "true");
        window.setTimeout(() => this.closeBtn?.focus(), 60);
    }

    private close(restoreFocus = true): void {
        if (!this.isOpen()) return;
        this.nav.classList.remove("open");
        this.overlay?.classList.remove("open");
        document.body.classList.remove("sf-menu-open");
        this.toggle.setAttribute("aria-expanded", "false");
        if (restoreFocus) this.toggle.focus();
    }
}

/**
 * The header search placeholder is ~390px of text at 16px; on a phone the field is ~250px,
 * so it was cut off mid-word. Swap in the shorter data-placeholder-short wording there
 * (storefront.css also sets the placeholder to 14px; the typed text stays 16px so iOS
 * doesn't zoom the page on focus).
 */
function initSearchPlaceholder(): void {
    const input = document.querySelector<HTMLInputElement>(".sf-header-search input[data-placeholder-short]");
    if (!input) return;
    const full = input.placeholder;
    const short = input.dataset.placeholderShort || full;
    const phone = window.matchMedia("(max-width: 600px)");
    const apply = () => { input.placeholder = phone.matches ? short : full; };
    apply();
    phone.addEventListener("change", apply);
}

/**
 * Phones pin only the slim header bar (☰ · logo · icons); the search box scrolls away with
 * the page. Once it's off screen, body gets .sf-search-offscreen, which reveals a 🔍 button
 * in the bar that scrolls back up and focuses the search field.
 */
function initSearchJump(): void {
    const form = document.querySelector<HTMLElement>('.sf-header-search');
    const input = form?.querySelector<HTMLInputElement>('input[name="search"]');
    const btn = document.querySelector<HTMLButtonElement>('[data-action="focus-search"]');
    if (!form || !input || !btn) return;

    // "Off screen" = scrolled up behind the pinned bar. A passive scroll listener (rather than
    // an IntersectionObserver) so it can account for the bar's height.
    const bar = document.querySelector<HTMLElement>('.sf-header-bar');
    const update = () => {
        const hidden = form.getBoundingClientRect().bottom <= (bar?.offsetHeight ?? 0);
        document.body.classList.toggle('sf-search-offscreen', hidden);
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    btn.addEventListener('click', () => {
        // Focus inside the click (iOS only opens the keyboard from a user gesture), then scroll.
        input.focus({ preventScroll: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    });
}

function init(): void {
    new StorefrontMenu();
    initSearchPlaceholder();
    initSearchJump();
    initSearchSuggest();
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
else init();
